import { useContext } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { apiGet, apiPost, apiPatch, apiDelete } from 'api';
import AuthContext from 'context/auth-context';

const getDataAccesses = async (token) => {
  const url = 'admin/data_accesses';
  const data = await apiGet({ url, token });
  return data;
};

const getDataAccess = async (token, id) => {
  const url = `admin/data_accesses/${id}`;
  const data = await apiGet({ url, token });
  return data;
};

const createDataAccess = async (token, params) => {
  const url = 'admin/data_accesses';
  const data = await apiPost({ url, token, objectName: 'data_access', params });
  return data;
};

const updateDataAccess = async (token, id, params) => {
  const url = `admin/data_accesses/${id}`;
  const data = await apiPatch({ url, token, objectName: 'data_access', params });
  return data;
};

const deleteDataAccess = async (token, id) => {
  const url = `admin/data_accesses/${id}`;
  const data = await apiDelete({ url, token });
  return data;
};

export function useAdminDataAccesses({ options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getDataAccesses(token);
  const queryKey = 'admin-data-accesses';
  return useQuery(queryKey, queryFn, { ...options });
}

export function useAdminDataAccess({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryFn = () => getDataAccess(token, id);
  const queryKey = ['admin-data-access', id];
  return useQuery(queryKey, queryFn, { enabled: !!id, ...options });
}

export function useCreateAdminDataAccess({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => createDataAccess(token, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-data-accesses');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useUpdateAdminDataAccess({ id, options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (params) => updateDataAccess(token, id, params),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-data-accesses');
        queryClient.invalidateQueries(['admin-data-access', id]);
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}

export function useDeleteAdminDataAccess({ options } = {}) {
  const token = useContext(AuthContext);
  const queryClient = useQueryClient();

  return useMutation(
    (id) => deleteDataAccess(token, id),
    {
      ...options,
      onSuccess: (data) => {
        queryClient.invalidateQueries('admin-data-accesses');
        if (options?.onSuccess) {
          options.onSuccess(data);
        }
      }
    }
  );
}
